// components/avances/JournalActionsPrivilegiees.jsx
// ============================================================================
// JOURNAL DES ACTIONS PRIVILÉGIÉES
// ============================================================================
// Lecture seule : une ligne par étape (demande, refus, exécution), écrite par
// le serveur et jamais modifiée. Ouvert depuis les avances sur salaire, pour
// qui détient le passe-droit ou peut défiger un état des salaires.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Loader2, ScrollText } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { annulationPrivilegieeApi } from './annulationPrivilegieeApi';

const BOUTON_PRINCIPAL = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-md bg-[#246990] px-4 text-sm font-medium text-white transition hover:bg-[#1f5a7a]';

const ETAPES = {
  demande: { libelle: 'Demande', classe: 'bg-[#eef7fb] text-[#246990]' },
  refus: { libelle: 'Refus', classe: 'bg-gray-100 text-gray-700' },
  execution: { libelle: 'Exécution', classe: 'bg-red-50 text-red-700' }
};

const formaterMontant = (montant) => (montant === null || montant === undefined
  ? '-'
  : `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant)))} FCFA`);

const formaterDateHeure = (valeur) => {
  if (!valeur) return '-';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime())
    ? String(valeur)
    : date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const JournalActionsPrivilegiees = ({ open, onClose }) => {
  const [lignes, setLignes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let annule = false;
    setChargement(true);
    annulationPrivilegieeApi.journal()
      .then((donnees) => { if (!annule) setLignes(donnees); })
      .catch((error) => { if (!annule) setErreur(error.message); })
      .finally(() => { if (!annule) setChargement(false); });
    return () => { annule = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(ouvert) => { if (!ouvert) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <ScrollText size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">Journal des actions privilégiées</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Chaque demande, refus et exécution, dans l'ordre. Rien n'y est jamais modifié.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {chargement ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="animate-spin text-[#246990]" size={26} />
            </div>
          ) : erreur ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</div>
          ) : lignes.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">Aucune action privilégiée pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium">Étape</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Employé concerné</th>
                    <th className="px-4 py-3 font-medium">Par</th>
                    <th className="px-4 py-3 text-right font-medium">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lignes.map((ligne) => {
                    const etape = ETAPES[ligne.etape] || { libelle: ligne.etape, classe: 'bg-gray-100 text-gray-700' };
                    return (
                      <tr key={ligne.id} className="align-top">
                        <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formaterDateHeure(ligne.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex h-6 w-[90px] items-center justify-center rounded-full text-xs font-medium ${etape.classe}`}>
                            {etape.libelle}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          {ligne.resume}
                          {Number(ligne.auto_validee) === 1 && (
                            <span className="ml-2 inline-flex h-5 items-center rounded-full border border-amber-200 bg-amber-50 px-2 text-[10px] font-semibold text-amber-800">
                              Auto-validée
                            </span>
                          )}
                          {ligne.support_request_id && (
                            <span className="block text-[11px] text-gray-500">Demande n° {ligne.support_request_id}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{ligne.employe_concerne || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{ligne.acteur_nom || '-'}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-gray-900">{formaterMontant(ligne.montant)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <button type="button" onClick={onClose} className={BOUTON_PRINCIPAL}>Fermer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JournalActionsPrivilegiees;
